import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { emailSchema, validate } from '../../utils/validators';
import { logger, generateRequestId } from '../../utils/logger';

// Rate limiting for password reset attempts
const resetAttempts = {
  attempts: [],
  maxAttempts: 3,
  windowMs: 15 * 60 * 1000 // 15 minutes
};

const checkRateLimit = () => {
  const now = Date.now();
  const recentAttempts = resetAttempts?.attempts?.filter(time => now - time < resetAttempts?.windowMs);
  resetAttempts.attempts = recentAttempts;
  
  if (recentAttempts?.length >= resetAttempts?.maxAttempts) {
    const oldestAttempt = Math.min(...recentAttempts);
    const waitTime = Math.ceil((resetAttempts?.windowMs - (now - oldestAttempt)) / 1000 / 60);
    return { allowed: false, waitTime };
  }
  
  return { allowed: true };
};

const recordAttempt = () => {
  resetAttempts?.attempts?.push(Date.now());
};

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);

  const validateForm = () => {
    const validation = validate(emailSchema, email);
    
    if (!validation?.success) {
      setErrors({ email: validation?.errors });
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleChange = (e) => {
    const value = e?.target?.value;
    
    // Input sanitization - remove any control characters
    const sanitizedValue = value?.replace(/[\r\n\0]/g, '');
    
    setEmail(sanitizedValue);
    
    // Clear errors when user starts typing
    if (errors?.email) {
      setErrors({});
    }
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Check rate limiting
    const rateLimit = checkRateLimit();
    if (!rateLimit?.allowed) {
      setGeneralError(`Too many reset attempts. Please try again in ${rateLimit?.waitTime} minutes.`);
      logger?.warn('Password reset rate limit exceeded', { email });
      return;
    }
    
    setLoading(true);
    setGeneralError('');
    
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      recordAttempt();
      
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/auth/confirm?next=/reset-password`
      });
      
      if (error) {
        // Generic error message to prevent user enumeration
        setGeneralError('Unable to process your request. Please try again later.');
        logger?.warn('Password reset request failed', { email, error: error?.message, requestId });
      } else {
        // Always show success message (even if email doesn't exist) to prevent user enumeration
        setIsSubmitted(true);
        logger?.audit('PASSWORD_RESET_REQUESTED', null, { email, requestId });
        
        // Start resend cooldown
        setCanResend(false);
        setResendCooldown(60);
        
        const cooldownInterval = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) {
              clearInterval(cooldownInterval);
              setCanResend(true);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      logger?.error('Password reset error', error, { email, requestId });
      setGeneralError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    
    setCanResend(false);
    setResendCooldown(60);
    
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`
      });
      
      if (!error) {
        logger?.audit('PASSWORD_RESET_RESENT', null, { email, requestId });
      }
      
      // Start cooldown again
      const cooldownInterval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownInterval);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      logger?.error('Password reset resend error', error, { email, requestId });
      setCanResend(true);
    }
  };

  const handleBackToForm = () => {
    setIsSubmitted(false);
    setEmail('');
    setErrors({});
    setGeneralError('');
  };

  return (
    <>
      <Helmet>
        <title>Forgot Password - MyBodyToday</title>
        <meta name="description" content="Reset your password securely with email verification" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Back to Sign In Link */}
          <div className="mb-6">
            <Link
              to="/sign-in"
              className="inline-flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 transition-colors"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Sign In
            </Link>
          </div>

          {/* Main Card */}
          <div className="bg-card rounded-lg shadow-therapeutic-lg border border-border p-6 sm:p-8">
            {!isSubmitted ? (
              <>
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100 mb-4">
                    <Icon name="KeyRound" size={24} className="text-teal-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground mb-2">
                    Forgot Password?
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    No worries! Enter your email address and we'll send you a secure link to reset your password.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* General Error Message */}
                  {generalError && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                      <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-destructive">{generalError}</p>
                    </div>
                  )}

                  {/* Email Input */}
                  <Input
                    type="email"
                    name="email"
                    label="Email Address"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={handleChange}
                    error={errors?.email}
                    required
                    disabled={loading}
                    autoComplete="email"
                    maxLength={255}
                    autoFocus
                  />

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    loading={loading}
                    disabled={loading}
                    size="lg"
                  >
                    {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
                  </Button>
                </form>

                {/* Security Note */}
                <div className="mt-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Icon name="Shield" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-blue-900 mb-1">
                        Security Information
                      </h3>
                      <p className="text-xs text-blue-700">
                        For your security, password reset links expire after 1 hour and can only be used once.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Success State */}
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                    <Icon name="Mail" size={32} className="text-green-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground mb-2">
                    Check Your Email
                  </h1>
                  <p className="text-sm text-muted-foreground mb-6">
                    We've sent a password reset link to <strong className="text-foreground">{email}</strong>
                  </p>

                  {/* Instructions */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                    <h3 className="text-sm font-medium text-blue-900 mb-3 flex items-center gap-2">
                      <Icon name="Info" size={16} className="text-blue-600" />
                      Next Steps
                    </h3>
                    <ol className="space-y-2 text-sm text-blue-700">
                      <li className="flex items-start gap-2">
                        <span className="font-medium">1.</span>
                        <span>Check your email inbox for a message from MyBodyToday</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-medium">2.</span>
                        <span>Click the secure reset link in the email</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-medium">3.</span>
                        <span>Create a new password for your account</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-medium">4.</span>
                        <span>Check your spam folder if you don't see the email within 5 minutes</span>
                      </li>
                    </ol>
                  </div>

                  {/* Resend Option */}
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Didn't receive the email?
                    </p>
                    <Button
                      onClick={handleResend}
                      variant="outline"
                      fullWidth
                      disabled={!canResend}
                    >
                      {canResend ? 'Resend Reset Link' : `Resend in ${resendCooldown}s`}
                    </Button>
                    <Button
                      onClick={handleBackToForm}
                      variant="ghost"
                      fullWidth
                    >
                      Try Different Email
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Additional Help */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Need help?{' '}
              <Link to="/sign-in" className="text-teal-600 hover:text-teal-700 font-medium">
                Contact Support
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;